// ADMIN-INSIGHTS (design docs/design/2026-10-30-admin-insights-agent.md, v1 "ask, answer, pin").
// An owners-and-admins-only "Ask" panel over a fixed metrics catalogue, and the answers pinned to
// Home as small widgets the `metrics.snapshot` job refreshes.
//
//   - The catalogue is the semantic layer: a measure × a breakdown × a period, nothing else. No
//     free SQL, no text-to-SQL; a question outside it is answered "not in the catalogue" with the
//     nearest measures offered.
//   - Every answer is read as the asker through the same route gate (`manager`): owners, admins and
//     agency operators. Members never see the panel, the widgets or these routes.
//   - The Chief of Staff reads the same layer (`agent/metrics`, the MCP `metrics_query`), so its
//     check-ins and weekly report quote the numbers Ask pins.
import { z } from "zod";
import {
  InsightAskVia,
  InsightBy,
  InsightMeasure,
  InsightPeriod,
  InsightVisibility,
  InsightViz,
} from "../enums";
import { IsoDate, Timestamp, Uuid } from "../primitives";
import { CompanyParams } from "./common";
import { defineRoute } from "./route";

export type InsightMeasureKey = z.infer<typeof InsightMeasure>;
export type InsightByKey = z.infer<typeof InsightBy>;
export type InsightPeriodKey = z.infer<typeof InsightPeriod>;

/** What one catalogue measure is, and how it may be asked. */
export type InsightMeasureDef = {
  label: string;
  unit: "money" | "count" | "percent";
  /** `flow`: counted over a period; `state`: now, with daily snapshots as its history. */
  kind: "flow" | "state";
  by: readonly InsightByKey[];
  info: string;
  /** The web page the number comes from (relative to the company). */
  link: string;
};

/** The metrics catalogue (design §4, first set). */
export const INSIGHT_CATALOG: Readonly<Record<InsightMeasureKey, InsightMeasureDef>> = {
  agent_spend: {
    label: "Agent spend",
    unit: "money",
    kind: "flow",
    by: ["none", "agent", "day", "week"],
    info: "What the DEs and agents cost, from the cost ledger (summed per day by spend.snapshot).",
    link: "/kpis/reports/finance/agent-spend",
  },
  budget_use: {
    label: "Budget used",
    unit: "percent",
    kind: "state",
    by: ["agent"],
    info: "Each DE's share of its current budget used (in the budget's own period), with its limit and pace.",
    link: "/kpis/reports/finance/agent-spend",
  },
  tasks_done: {
    label: "Tasks done",
    unit: "count",
    kind: "flow",
    by: ["none", "assignee_kind", "assignee", "day", "week"],
    info: "Tasks finished in the period, by who they are assigned to (people, DEs, agents).",
    link: "/work",
  },
  tasks_created: {
    label: "Tasks created",
    unit: "count",
    kind: "flow",
    by: ["none", "assignee_kind", "assignee", "day", "week"],
    info: "Tasks created in the period.",
    link: "/work",
  },
  tasks_open: {
    label: "Open tasks",
    unit: "count",
    kind: "state",
    by: ["none", "assignee_kind", "assignee", "day", "week"],
    info: "Tasks not done yet, on boards that are not archived.",
    link: "/work",
  },
  tasks_in_progress: {
    label: "Tasks in progress",
    unit: "count",
    kind: "state",
    by: ["none", "assignee_kind", "assignee", "day", "week"],
    info: "Open tasks in a status the board marks as started.",
    link: "/work",
  },
  tasks_overdue: {
    label: "Overdue tasks",
    unit: "count",
    kind: "state",
    by: ["none", "assignee_kind", "assignee", "day", "week"],
    info: "Open tasks whose due date has passed.",
    link: "/work",
  },
  approvals_waiting: {
    label: "Approvals waiting",
    unit: "count",
    kind: "state",
    by: ["none", "day", "week"],
    info: "Approvals still waiting for a person.",
    link: "/inbox",
  },
};

export const INSIGHT_PERIOD_LABEL: Readonly<Record<InsightPeriodKey, string>> = {
  today: "today",
  this_week: "this week",
  last_week: "last week",
  this_month: "this month",
  last_month: "last month",
  last_30_days: "the last 30 days",
  this_quarter: "this quarter",
};

export const INSIGHT_BY_LABEL: Readonly<Record<InsightByKey, string>> = {
  none: "total",
  agent: "per DE / agent",
  assignee_kind: "people vs DEs vs agents",
  assignee: "per assignee",
  day: "per day",
  week: "per week",
};

/** One catalogue question: a measure, its breakdown, its period. */
export const InsightSpec = z
  .strictObject({
    measure: InsightMeasure,
    by: InsightBy.default("none"),
    period: InsightPeriod.default("this_week"),
  })
  // `none` (the default) means the measure's own default breakdown (budgets: per DE).
  .refine((s) => s.by === "none" || INSIGHT_CATALOG[s.measure].by.includes(s.by), {
    message: "this breakdown is not offered for this measure",
    path: ["by"],
  })
  .meta({ id: "InsightSpec" });
export type InsightSpec = z.infer<typeof InsightSpec>;

/** `GET insights/query`: the spec as query parameters. */
export const InsightQuery = z.object({
  measure: InsightMeasure,
  by: InsightBy.optional(),
  period: InsightPeriod.optional(),
});

export const InsightRow = z
  .object({
    key: z.string(),
    label: z.string(),
    /** Who the row is (people / DE / AG / nobody), when the breakdown is by assignee or agent. */
    kind: z.enum(["person", "de", "ag", "unassigned"]).nullable(),
    value: z.number(),
    /** A short second line (a DE's limit and pace, a seat). */
    detail: z.string().nullable(),
  })
  .meta({ id: "InsightRow" });

export const InsightResult = z
  .object({
    spec: z.object({ measure: InsightMeasure, by: InsightBy, period: InsightPeriod }),
    title: z.string(),
    unit: z.enum(["money", "count", "percent"]),
    /** Money only: the reporting currency (amounts in its minor units). */
    currency: z.string().nullable(),
    from: IsoDate,
    to: IsoDate,
    /** The headline number (null: none, e.g. budget use, or no data yet). */
    total: z.number().nullable(),
    /** A flow measure's total over the period before (same length); null otherwise. */
    previous_total: z.number().nullable(),
    rows: z.array(InsightRow),
    computed_at: Timestamp,
    /** One line of caveat or the honest empty state ("no tagged spend yet"). */
    note: z.string().nullable(),
    /** The web page the number comes from: under `/c/{slug}`, or `/inbox` (the person's). */
    link: z.string(),
  })
  .meta({ id: "InsightResult" });
export type InsightResult = z.infer<typeof InsightResult>;

export const InsightCatalog = z
  .object({
    measures: z.array(
      z.object({
        measure: InsightMeasure,
        label: z.string(),
        unit: z.enum(["money", "count", "percent"]),
        kind: z.enum(["flow", "state"]),
        by: z.array(InsightBy),
        info: z.string(),
      }),
    ),
    periods: z.array(InsightPeriod),
    /** Questions people can tap to start with. */
    examples: z.array(z.string()),
  })
  .meta({ id: "InsightCatalog" });

export const InsightAskBody = z
  .strictObject({ question: z.string().trim().min(1).max(500) })
  .meta({ id: "InsightAskBody" });

export const InsightAnswer = z
  .object({
    id: Uuid,
    question: z.string(),
    /** The short answer: the number, the comparison, one caveat. */
    answer: z.string(),
    via: InsightAskVia,
    spec: InsightSpec.nullable(),
    result: InsightResult.nullable(),
    /** Catalogue questions close to this one (when it was not understood). */
    suggestions: z.array(z.string()),
    created_at: Timestamp,
  })
  .meta({ id: "InsightAnswer" });
export type InsightAnswer = z.infer<typeof InsightAnswer>;

export const InsightAskList = z
  .object({
    data: z.array(InsightAnswer.omit({ result: true })),
    /** Questions left today (per person) under the daily caps. */
    left_today: z.int().min(0),
  })
  .meta({ id: "InsightAskList" });

export const InsightWidget = z
  .object({
    id: Uuid,
    title: z.string(),
    spec: InsightSpec,
    viz: InsightViz,
    visibility: InsightVisibility,
    /** The owner or admin who pinned it. */
    owner: z.object({ membership_id: Uuid, name: z.string() }),
    /** Whether the reader may remove it (its owner; any owner or admin for an `admins` one). */
    can_remove: z.boolean(),
    /** The answer as the scheduled refresh last computed it (null: not computed yet). */
    result: InsightResult.nullable(),
    refreshed_at: Timestamp.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "InsightWidget" });
export type InsightWidget = z.infer<typeof InsightWidget>;

export const InsightWidgetList = z
  .object({ data: z.array(InsightWidget) })
  .meta({ id: "InsightWidgetList" });

export const InsightWidgetCreate = z
  .strictObject({
    title: z.string().trim().min(1).max(120),
    spec: InsightSpec,
    viz: InsightViz.optional(),
    visibility: InsightVisibility.optional(),
  })
  .meta({ id: "InsightWidgetCreate" });

const WidgetParams = z.strictObject({ co: Uuid, widget_id: Uuid });

const insights = { feature: "core", tag: "insights", access: "manager" } as const;
const base = "/v1/companies/{co}";

export const insightRoutes = {
  "insights.catalog": defineRoute({
    ...insights,
    id: "insights.catalog",
    method: "get",
    path: `${base}/insights/catalog`,
    summary:
      "The metrics catalogue Ask answers from: measures, breakdowns, periods (owners and admins)",
    params: CompanyParams,
    response: { status: 200, schema: InsightCatalog },
    errors: [],
    emits: [],
  }),
  "insights.query": defineRoute({
    ...insights,
    id: "insights.query",
    method: "get",
    path: `${base}/insights/query`,
    summary: "One catalogue measure, broken down, over a period (owners and admins)",
    description:
      "Computed from the tables the pages read: the cost ledger's daily agent spend, budgets and " +
      "pace, tasks and their assignees' holders, approvals. State measures per day or week read " +
      "the daily `metric_snapshot` rows.",
    params: CompanyParams,
    query: InsightQuery,
    response: { status: 200, schema: InsightResult },
    errors: ["validation_failed"],
    emits: [],
  }),
  "insights.ask": defineRoute({
    ...insights,
    id: "insights.ask",
    method: "post",
    path: `${base}/insights/ask`,
    summary:
      "Ask a question about the company; answered from the metrics catalogue, kept 30 days for the asker (owners and admins)",
    description:
      "The question is mapped to one catalogue spec by rules first and by a small model only when " +
      "the rules do not understand it; the numbers and the wording come from the catalogue, " +
      "never the model. Daily caps per person and per company.",
    params: CompanyParams,
    body: InsightAskBody,
    idempotency: "optional",
    response: { status: 200, schema: InsightAnswer },
    errors: ["rate_limited"],
    emits: ["insight.asked"],
  }),
  "insights.asks": defineRoute({
    ...insights,
    id: "insights.asks",
    method: "get",
    path: `${base}/insights/asks`,
    summary: "The caller's questions of the last 30 days, newest first (owners and admins)",
    params: CompanyParams,
    response: { status: 200, schema: InsightAskList },
    errors: [],
    emits: [],
  }),
  "insights.widgets.list": defineRoute({
    ...insights,
    id: "insights.widgets.list",
    method: "get",
    path: `${base}/insights/widgets`,
    summary: "The answers pinned to the caller's Home: theirs, and those pinned for every admin",
    params: CompanyParams,
    response: { status: 200, schema: InsightWidgetList },
    errors: [],
    emits: [],
  }),
  "insights.widgets.create": defineRoute({
    ...insights,
    id: "insights.widgets.create",
    method: "post",
    path: `${base}/insights/widgets`,
    summary: "Pin an answer to Home as a widget, refreshed on a schedule (owners and admins)",
    params: CompanyParams,
    body: InsightWidgetCreate,
    idempotency: "optional",
    response: { status: 201, schema: InsightWidget },
    errors: ["validation_failed", "conflict"],
    emits: ["insight_widget.changed"],
  }),
  "insights.widgets.delete": defineRoute({
    ...insights,
    id: "insights.widgets.delete",
    method: "delete",
    path: `${base}/insights/widgets/{widget_id}`,
    summary: "Unpin a widget (its owner; any owner or admin for one pinned for every admin)",
    params: WidgetParams,
    response: { status: 204 },
    errors: ["not_found"],
    emits: ["insight_widget.changed"],
  }),
  "agent.metrics": defineRoute({
    feature: "core",
    tag: "digital-employees",
    id: "agent.metrics",
    method: "get",
    path: `${base}/agent/metrics`,
    summary: "metrics_query: one catalogue measure for the Chief of Staff's check-ins and report",
    description:
      "The same catalogue as Ask (`insights.query`). Only the company's Chief of Staff reads it; " +
      "any other DE is refused.",
    access: "run_token",
    params: CompanyParams,
    query: InsightQuery,
    response: { status: 200, schema: InsightResult },
    errors: ["validation_failed"],
    emits: [],
  }),
} as const;
